/**
 * InlineTimeField.js
 *
 * FT-284: shared inline event-time field for in-voyage data modals. Mounted
 * above the action row of every Position / Weather / Fuel / Note / Operation
 * Start modal. Replaces the previous TimePickerModal "Step 2" gate so the
 * 95% case (event just happened) is a single tap.
 *
 * Two visual states (driven by `timeField.state` from useInlineEventTime):
 *
 *   state === 'now'      → "Time: HH:MM (now)" badge + pencil edit button.
 *   state === 'editing'  → date + time inputs + reset button +
 *                          band-conditional reason field.
 *
 * Bands (driven by `timeField.band`):
 *
 *   'none'        → no reason input.
 *   'free_text'   → 30 min .. 4h backdate. Free-text input, ≥3 chars after trim.
 *   'structured'  → ≥4h backdate. Structured dropdown, conditional free-text
 *                   when reasonCode === 'other'.
 *
 * Accessibility:
 *   - Outer wrapper: role="status" aria-live="polite" so SR users hear the
 *     now→editing transition.
 *   - Pencil and reset buttons carry descriptive aria-labels.
 *   - Future-time inline error renders inside the field with role="alert".
 *
 * Layout-agnostic. Works inside <form> or outside it (FT-283 uses no <form>).
 */

import React, { useMemo } from 'react';
import { Pencil, RotateCcw, AlertTriangle } from 'lucide-react';
import {
  formatDateInTimezone,
  getShortOffsetLabel
} from '../../../../../utils/timezoneUtils';
import { STRUCTURED_REASONS } from './TimePickerModal';

const InlineTimeField = ({
  timeField,
  vesselTimezone,
  label = 'Time',
  disabled = false,
  nowBadgeLabel = 'now'
}) => {
  const tz = vesselTimezone || timeField?.tz || 'UTC';

  // Display HH:MM for the badge in the vessel's timezone.
  const badgeTime = useMemo(() => {
    if (!timeField?.eventTime) return '';
    try {
      return formatDateInTimezone(timeField.eventTime, tz, { format: 'time' });
    } catch {
      return '';
    }
  }, [timeField?.eventTime, tz]);

  const offsetLabel = useMemo(() => {
    try {
      return getShortOffsetLabel(tz);
    } catch {
      return '';
    }
  }, [tz]);

  // UTC HH:MM equivalent for the editing footnote.
  const utcHint = useMemo(() => {
    if (!timeField?.eventTime) return '';
    try {
      return formatDateInTimezone(timeField.eventTime, 'UTC', { format: 'time' });
    } catch {
      return '';
    }
  }, [timeField?.eventTime]);

  if (!timeField) return null;

  const {
    state,
    band,
    isFuture,
    errorMessage,
    localDate,
    localTime,
    reasonCode,
    reasonText,
    reasonOtherNote,
    setEditing,
    setDateTime,
    setReasonText,
    setReasonCode,
    setReasonOtherNote,
    reset
  } = timeField;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}
    >
      {state === 'now' ? (
        // ----------------------- 'now' badge --------------------------
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-medium text-slate-700">
            <span className="text-slate-500 mr-1">{label}:</span>
            <span className="font-mono">{badgeTime}</span>
            <span className="text-slate-500 ml-2 text-xs">({nowBadgeLabel})</span>
            {offsetLabel && (
              <span className="text-slate-400 ml-2 text-xs">{offsetLabel}</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label="Edit event time"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-slate-600 border border-slate-300 rounded-md hover:bg-slate-100 transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
        </div>
      ) : (
        // ----------------------- 'editing' inputs ---------------------
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm font-medium text-slate-700">{label}</label>
            <button
              type="button"
              onClick={reset}
              aria-label="Reset to now"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-slate-600 border border-slate-300 rounded-md hover:bg-slate-100 transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset to now
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-slate-500 mb-1" htmlFor="inline-event-date">Date</label>
              <input
                id="inline-event-date"
                type="date"
                value={localDate}
                onChange={(e) => setDateTime(e.target.value, localTime)}
                className="block w-full h-11 px-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1" htmlFor="inline-event-time">Time (24h)</label>
              <input
                id="inline-event-time"
                type="time"
                value={localTime}
                onChange={(e) => setDateTime(localDate, e.target.value)}
                className="block w-full h-11 px-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
              />
            </div>
          </div>

          {utcHint && (
            <p className="text-xs text-slate-500">
              {tz} {offsetLabel}. UTC equivalent: {utcHint}
            </p>
          )}

          {/* Future-time inline error */}
          {isFuture && (
            <div
              role="alert"
              className="flex items-start gap-2 p-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{errorMessage || 'Time cannot be in the future.'}</span>
            </div>
          )}

          {/* Band-specific reason inputs */}
          {!isFuture && band === 'free_text' && (
            <div>
              <label htmlFor="inline-event-reason-text" className="block text-sm font-medium text-slate-700 mb-1">
                Reason for delay <span className="text-red-500">(required)</span>
              </label>
              <input
                id="inline-event-reason-text"
                type="text"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder="Briefly explain why the time is in the past"
                maxLength={500}
                className="block w-full h-11 px-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
              />
              {reasonText.trim().length > 0 && reasonText.trim().length < 3 && (
                <p className="mt-1 text-xs text-red-600">
                  Please enter a reason for the delay (at least 3 characters).
                </p>
              )}
            </div>
          )}

          {!isFuture && band === 'structured' && (
            <div className="space-y-2">
              <label htmlFor="inline-event-reason-code" className="block text-sm font-medium text-slate-700">
                Reason for delay <span className="text-red-500">(required)</span>
              </label>
              <select
                id="inline-event-reason-code"
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
                className="block w-full h-11 px-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
              >
                <option value="">Select a reason...</option>
                {STRUCTURED_REASONS.map(r => (
                  <option key={r.code} value={r.code}>{r.label}</option>
                ))}
              </select>
              {reasonCode === 'other' && (
                <input
                  type="text"
                  value={reasonOtherNote}
                  onChange={(e) => setReasonOtherNote(e.target.value)}
                  placeholder="Specify..."
                  maxLength={500}
                  className="block w-full h-11 px-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InlineTimeField;
